import { useEffect, useRef } from 'react';
import { ConnectNotificationBanner } from '@stripe/react-connect-js';
import { Dialog, DialogPortal, DialogContent, DialogTitle, DialogDescription, DialogHeader } from '../../components/ui/dialog';
import { Button } from '@/components/ui/button';
import StripeEmbedBoundary from './StripeEmbedBoundary';
import { STRIPE_DASHBOARD_NOTIFICATION_COLLECTION_OPTIONS } from './stripeConnectCollectionOptions';

/** One mounted Stripe component. Closed content is clipped and inert, not reparented. */
export default function StripeRequirementsReview({ open, onOpenChange, enabled, generation, phase, total, onNotificationsChange, onError, onRetry, onContinueSetup }) {
  const closeRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const trigger = document.activeElement;
    const root = document.getElementById('root');
    const wasInert = root?.hasAttribute('inert');
    root?.setAttribute('inert', '');
    closeRef.current?.focus();
    return () => {
      if (!wasInert) root?.removeAttribute('inert');
      if (trigger?.isConnected && !trigger.closest('[inert]')) trigger.focus?.();
    };
  }, [open]);
  return <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
    <DialogPortal>{open && <div className="fixed inset-0 z-50 drawer-frosted-overlay" aria-hidden="true" onMouseDown={() => onOpenChange(false)} />}</DialogPortal>
    <DialogContent forceMount showX={false} aria-modal={open || undefined} data-stripe-notification-phase={phase}
      aria-hidden={!open || undefined} inert={!open ? '' : undefined}
      onOpenAutoFocus={(event) => { event.preventDefault(); if (open) closeRef.current?.focus(); }}
      onInteractOutside={(event) => event.preventDefault()}
      className={`z-[51] flex w-full sm:w-[calc(100%-2rem)] max-w-2xl flex-col gap-0 overflow-hidden rounded-md p-0 ${open ? 'max-h-[85dvh]' : 'pointer-events-none h-0 max-h-0 overflow-hidden border-0 opacity-0 shadow-none'}`}>
      <DialogHeader className="shrink-0 border-b border-edge bg-paper-muted px-5 py-4 pr-20 text-left">
        <DialogTitle>Stripe account requirements</DialogTitle>
        <DialogDescription>Review Stripe’s requests and complete any required steps.</DialogDescription>
        <button ref={closeRef} type="button" onClick={() => onOpenChange(false)} className="absolute right-5 top-4 text-sm text-ink-secondary">Close</button>
      </DialogHeader>
      <div className="min-h-0 overflow-y-auto px-0 py-6 sm:px-5">
        {phase === 'loading' && <p role="status" className="text-sm text-ink-secondary">Loading Stripe requests…</p>}
        {phase === 'error' && <div role="status" className="space-y-3 text-sm text-ink-secondary">
          <p>Stripe’s requests couldn’t load. Your saved payment status has not changed.</p>
          <Button variant="secondary" size="workspace" onClick={onRetry}>Try again</Button>
        </div>}
        {enabled && <StripeEmbedBoundary key={generation} onRetry={onRetry} onError={onError}>
          <ConnectNotificationBanner collectionOptions={STRIPE_DASHBOARD_NOTIFICATION_COLLECTION_OPTIONS}
            onNotificationsChange={onNotificationsChange} onLoadError={onError} />
        </StripeEmbedBoundary>}
        {((phase === 'ready' && total === 0) || !enabled) && <div className="space-y-3 text-sm text-ink-secondary">
          <p>{phase === 'error' ? 'You can also try reopening Stripe setup.' : 'There are no detailed Stripe notifications available here. You can review your account information in Stripe setup.'}</p>
          <Button variant="secondary" size="workspace" onClick={onContinueSetup}>Review Stripe setup</Button>
        </div>}
      </div>
    </DialogContent>
  </Dialog>;
}
